/**
 * CheckInShell — 五个 Check-in 步骤页面的共享布局。
 *
 * 封装内容：全屏深蓝渐变 + 顶部 header（返回 / 关闭）+ 星星头像 + 底部 CTA 按钮栏。
 * 各步骤页面只需传入自己独特的内容（children），无需重复写 header / footer / 星星。
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, X } from 'lucide-react';
import { useAppSettings } from '../../../components/providers/AppSettingsProvider';
import { createPageUrl } from '../../../utils';

// ---------- Star avatar ----------

const starGlow = {
  background:
    'radial-gradient(circle, rgba(255,230,120,0.55) 0%, rgba(255,200,80,0.15) 45%, transparent 70%)',
};

/** Lumi 星星头像（所有步骤通用，统一 lg 尺寸） */
export function CheckInStarAvatar() {
  return (
    <div className="relative mt-4 flex h-[10rem] w-[10rem] shrink-0 items-center justify-center">
      {/* 光晕不超出容器边界，避免被父级 overflow-y-auto 裁切 */}
      <div className="absolute inset-0 rounded-full opacity-90 blur-2xl" style={starGlow} />
      <img
        src="/images/idle_star.png"
        alt=""
        width={192}
        height={192}
        className="relative z-[1] h-[6.5rem] w-[6.5rem] object-contain [filter:drop-shadow(0_0_14px_rgba(255,240,180,0.95))]"
        decoding="async"
      />
    </div>
  );
}

// ---------- CTA button ----------

const ctaClass =
  'rounded-full border border-white/30 bg-white/[0.18] px-10 py-3 text-center text-sm font-semibold text-white shadow-[0_0_0_1px_rgba(255,255,255,0.14),inset_0_1px_0_rgba(255,255,255,0.35)] backdrop-blur-xl backdrop-saturate-150 transition-all disabled:cursor-not-allowed disabled:opacity-40 active:scale-[0.97]';

// ---------- Shell ----------

interface CheckInShellProps {
  /** undefined → navigate(-1)；hideBack=true 时隐藏返回按钮 */
  onBack?: () => void;
  hideBack?: boolean;
  /** Primary CTA label */
  ctaLabel: string;
  onCta: () => void;
  ctaDisabled?: boolean;
  /** Extra padding-bottom class on the scroll area (default pb-40) */
  bodyPb?: string;
  children: React.ReactNode;
}

export function CheckInShell({
  onBack,
  hideBack = false,
  ctaLabel,
  onCta,
  ctaDisabled = false,
  bodyPb = 'pb-40',
  children,
}: CheckInShellProps) {
  const navigate = useNavigate();
  const { t } = useAppSettings();

  return (
    <div className="fixed inset-0 z-[70] flex flex-col overflow-hidden bg-gradient-to-b from-[#050a18] via-[#0b1430] to-[#2560b8] text-white">
      {/* Header */}
      <header
        className="flex shrink-0 items-center justify-between px-4 pb-2"
        style={{
          paddingTop:   'max(3rem, env(safe-area-inset-top))',
          paddingLeft:  'max(1rem, env(safe-area-inset-left))',
          paddingRight: 'max(1rem, env(safe-area-inset-right))',
        }}
      >
        {hideBack ? (
          <div className="h-10 w-10" aria-hidden />
        ) : (
          <button
            type="button"
            aria-label={t('checkIn.back')}
            className="flex h-10 w-10 items-center justify-center rounded-full text-white transition-opacity hover:bg-white/10 active:opacity-80"
            onClick={onBack ?? (() => navigate(-1))}
          >
            <ChevronLeft className="h-7 w-7" strokeWidth={2.25} />
          </button>
        )}
        <button
          type="button"
          aria-label={t('checkIn.close')}
          className="flex h-10 w-10 items-center justify-center rounded-full text-white transition-opacity hover:bg-white/10 active:opacity-80"
          onClick={() => navigate(createPageUrl('WhisperTalk'))}
        >
          <X className="h-6 w-6" strokeWidth={2.25} />
        </button>
      </header>

      {/* Scrollable body — 星星统一在此渲染，各页面只传自己的独特内容 */}
      <div className={`min-h-0 flex-1 overflow-y-auto px-4 pt-2 ${bodyPb}`}>
        <div className="mx-auto flex w-full max-w-md flex-col items-center">
          <CheckInStarAvatar />
          {children}
        </div>
      </div>

      {/* Bottom CTA */}
      <div
        className="shrink-0 flex justify-center bg-transparent px-4 pt-3"
        style={{
          paddingBottom: 'max(2.5rem, calc(env(safe-area-inset-bottom) + 1.25rem))',
        }}
      >
        <button
          type="button"
          disabled={ctaDisabled}
          onClick={onCta}
          className={ctaClass}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}
